import Empty from "@/components/empty-states/empty";
import { getUnicornRankings } from "@/services/api/UnicornTracker";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { Info } from "@phosphor-icons/react";
import CustomTooltip from "@/components/CustomTooltip";

export default function FastestUnicorn({
  regionData,
  countryData,
  selectedCountryName,
  selectedRegionName,
  unicornStats,
}) {
  const [fastestUnicorns, setFastestUnicorns] = useState([]);
  const [loading, setLoading] = useState(true);

  const searchParams = useSearchParams();
  const selectedRegionId = searchParams.get("unicorn_regions");
  const selectedCountryId = searchParams.get("country");
  const sectors = searchParams.get("sectors");
  const stages = searchParams.get("stages");
  const valuationCondition = searchParams.get("valuation_condition");
  const valuationAmount = searchParams.get("valuation_amount");
  const yearFoundedCondition = searchParams.get("year_founded_condition");
  const yearFoundedYear = searchParams.get("year_founded_year");
  const timeToUnicornMin = searchParams.get("min_time_to_unicorn");
  const timeToUnicornMax = searchParams.get("max_time_to_unicorn");
  const unicornStatus = searchParams.get("unicorn_status");
  // Fetch fastest unicorns data
  useEffect(() => {
    async function fetchFastestUnicorns() {
      try {
        setLoading(true);

        // Dynamic limit: show up to 5 unicorns, or fewer if total is less than 5
        const totalUnicorns = unicornStats?.total_unicorn || 0;
        const dynamicLimit = totalUnicorns > 0 ? Math.min(5, totalUnicorns) : 5;

        const params = {
          rank_by: "time_to_unicorn",
          order: "asc",
          limit: dynamicLimit,
        };

        if (selectedRegionId) params.unicorn_regions = selectedRegionId;
        if (selectedCountryId) params.countries = selectedCountryId;

        if (sectors) params.sectors = sectors;
        if (stages) params.stages = stages;

        if (valuationCondition && valuationAmount) {
          params.valuation_condition = valuationCondition;
          params.valuation_amount = valuationAmount;
        }

        if (yearFoundedCondition && yearFoundedYear) {
          params.year_founded_condition = yearFoundedCondition;
          params.year_founded_year = yearFoundedYear;
        }

        if (timeToUnicornMin) params.min_time_to_unicorn = timeToUnicornMin;
        if (timeToUnicornMax) params.max_time_to_unicorn = timeToUnicornMax;

        const statuses = unicornStatus
          ? unicornStatus.split(",").filter(Boolean)
          : ["current", "fallen", "alumni"];
        params.unicorn_status = statuses.join(",");
        if (statuses.includes("fallen")) params.include_lost_unicorn = 1;
        if (statuses.includes("alumni")) params.include_alumni = 1;
        if (!statuses.includes("current")) params.exclude_current_unicorn = 1;

        const response = await getUnicornRankings({ params });
        const rankings = response.data?.data?.rankings || [];

        setFastestUnicorns(rankings);
      } catch (error) {
        console.error("Error fetching fastest unicorns:", error);
        setFastestUnicorns([]);
      } finally {
        setLoading(false);
      }
    }

    fetchFastestUnicorns();
  }, [
    selectedRegionId,
    selectedCountryId,
    sectors,
    stages,
    valuationCondition,
    valuationAmount,
    yearFoundedCondition,
    yearFoundedYear,
    timeToUnicornMin,
    timeToUnicornMax,
    unicornStats,
    unicornStatus,
  ]);

  const getTitleText = () => {
    return "Fastest to Unicorn";
  };

  const formatTimeToUnicornYears = (unicorn) => {
    // Always use numeric value with 1 decimal for consistency with table
    const raw = unicorn?.time_to_unicorn;
    const num = Number(raw);
    if (!Number.isFinite(num) || num < 0) return "N/A";
    return `${num.toFixed(1)} years`;
  };

  // Calculate skeleton count for loading state
  const totalUnicorns = unicornStats?.total_unicorn || 0;
  const skeletonCount = totalUnicorns > 0 ? Math.min(5, totalUnicorns) : 5;

  return (
    <>
      <div className='flex items-center justify-between gap-1.5'>
        <h2 className='text-base font-medium sm:text-sm'>{getTitleText()}</h2>
        <CustomTooltip
          trigger={
            <span className='-mt-2 -mr-2 text-white/40'>
              <Info className='size-5' />
            </span>
          }
          content='Companies ranked by shortest time between founding and the unicorn event. Filters apply; change them to see the fastest in a sector, country, or status group. Based on current HQ.'
        />
      </div>

      <div className='pt-4 -my-2.5 divide-y divide-white/10'>
        {loading ? (
          Array.from({ length: skeletonCount }).map((_, index) => (
            <div key={index} className='flex items-center py-2.5 animate-pulse'>
              <div className='flex items-center flex-1 gap-3'>
                <div className='rounded-full size-9 bg-white/10' />
                <div>
                  <div className='w-16 h-3 mb-1 rounded bg-white/10' />
                  <div className='w-20 h-2 rounded bg-white/10' />
                </div>
              </div>
              <div className='h-3 rounded w-28 bg-white/10' />
            </div>
          ))
        ) : fastestUnicorns.length > 0 ? (
          fastestUnicorns.map((unicorn, index) => (
            <Link
              href={`/startups/${unicorn.slug}-${unicorn.id}`}
              target='_blank'
              key={unicorn.id || unicorn.uuid || index}
              className='flex items-center py-2.5 hover:bg-white/5 transition-colors -mx-4 px-4'
            >
              <div className='flex items-center flex-1 gap-3'>
                <div className='relative overflow-hidden rounded-full size-9 bg-white/5 ring-[3px] ring-white/15'>
                  {unicorn.logo_url ? (
                    <Image
                      fill
                      src={unicorn.logo_url}
                      alt={unicorn.name || "Startup logo"}
                      className='object-cover'
                      onError={(e) => {
                        if (e?.target) e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className='flex items-center justify-center w-full h-full text-xs font-bold bg-gradient-to-br from-yellow-400 to-red-500'>
                      {unicorn.name
                        ? unicorn.name.charAt(0).toUpperCase()
                        : "S"}
                    </div>
                  )}
                </div>

                <div className='min-w-0'>
                  <p className='text-sm font-semibold text-white truncate'>
                    {unicorn.name || "Unknown Startup"}
                  </p>
                  <p className='mt-0.5 text-xs text-white/50 truncate'>
                    Time to unicorn: {formatTimeToUnicornYears(unicorn)}
                  </p>
                </div>
              </div>
            </Link>
          ))
        ) : (
          <div className='mb-2 -mx-1 -mt-2'>
            <Empty
              title='Data Not Available'
              desc='No unicorns recorded for this selection.'
            />
          </div>
        )}
      </div>
    </>
  );
}
